import React, { useState } from 'react';
import '../css/PricingPage.css';
import { Check, Zap, Building2, Rocket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface PricingTier {
  name: string;
  icon: React.ReactNode;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  ctaText: string;
}

const PricingPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
    const navigate = useNavigate();


  const navigateToContactPage = () => {
    navigate('/contact');
  };

  const pricingTiers: PricingTier[] = [
    {
      name: 'Starter',
      icon: <Zap size={32} />,
      price: billingCycle === 'monthly' ? '$1,000' : '$800',
      period: billingCycle === 'monthly' ? '/month' : '/month (billed annually)',
      description: 'Perfect for small teams getting started with AI-powered claims processing',
      features: [
        'Around 1 claim per month',
        'Basic AI claims analysis',
        'Email support',
        'Standard processing speed',
        'Basic dashboard & reporting',
        'Single user access'
      ],
      ctaText: 'Start Free Trial'
    },
    {
      name: 'Professional',
      icon: <Building2 size={32} />,
      price: billingCycle === 'monthly' ? 'Coming Soon' : 'Coming Soon',
      period: billingCycle === 'monthly' ? '/month' : '/month (billed annually)',
      description: 'Ideal for growing insurance teams that need advanced automation',
      features: [
        'Up to 500 claims per month',
        'Advanced AI claims analysis',
        'Priority email & chat support',
        'Fast processing speed',
        'Advanced analytics & insights',
        'Up to 5 user seats',
        'Custom workflows',
        'API access'
      ],
      highlighted: true,
      ctaText: 'Start Free Trial'
    },
    {
      name: 'Enterprise',
      icon: <Rocket size={32} />,
      price: 'Custom',
      period: '',
      description: 'For large organizations requiring maximum scale and customization',
      features: [
        'Unlimited claims processing',
        'Custom AI model training',
        'Dedicated account manager',
        'Instant processing speed',
        'Custom reporting & dashboards',
        'Unlimited user seats',
        'Advanced security & compliance',
        'SLA guarantees',
        'White-label options',
        'On-premise deployment available'
      ],
      ctaText: 'Contact Sales'
    }
  ];

  return (
    <div className="pricing-section">
      <div className="pricing-content">
        <h2 className="pricing-title">Simple, Transparent Pricing</h2>
        <p className="pricing-subtitle">
          Choose the plan that's right for your team. All plans include a 14-day free trial.
        </p>

        {/* Billing Toggle */}
        <div className="billing-toggle">
          <button
            className={`toggle-option ${billingCycle === 'monthly' ? 'active' : ''}`}
            onClick={() => setBillingCycle('monthly')}
          >
            Monthly
          </button>
          <button
            className={`toggle-option ${billingCycle === 'annual' ? 'active' : ''}`}
            onClick={() => setBillingCycle('annual')}
          >
            Annual
            <span className="savings-badge">Save 20%</span>
          </button>
        </div>

        <div className="pricing-grid">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`pricing-card ${tier.highlighted ? 'highlighted' : ''}`}
            >
              {tier.highlighted && (
                <div className="popular-badge">Most Popular</div>
              )}
              
              <div className="pricing-header">
                <div className="pricing-icon">{tier.icon}</div>
                <h3 className="tier-name">{tier.name}</h3>
                <div className="price-container">
                  <span className="price">{tier.price}</span>
                  {tier.period && <span className="period">{tier.period}</span>}
                </div>
                <p className="tier-description">{tier.description}</p>
              </div>

              <div className="features-list">
                {tier.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="feature-item">
                    <Check size={20} className="check-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <button className="pricing-cta-button" onClick={navigateToContactPage}>
                {tier.ctaText}
              </button>
            </div>
          ))}
        </div>

        {/* FAQ Section */}
        <div className="pricing-faq">
          <h3 className="faq-title">Frequently Asked Questions</h3>
          <div className="faq-grid">
            <div className="faq-item">
              <h4>Can I change plans later?</h4>
              <p>Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.</p>
            </div>
            <div className="faq-item">
              <h4>What payment methods do you accept?</h4>
              <p>We accept all major credit cards, ACH transfers, and can arrange invoicing for Enterprise plans.</p>
            </div>
            <div className="faq-item">
              <h4>Is there a setup fee?</h4>
              <p>No setup fees for Starter and Professional plans. Enterprise plans include complimentary onboarding.</p>
            </div>
            <div className="faq-item">
              <h4>What happens after my trial ends?</h4>
              <p>Your trial converts to a paid subscription. You can cancel anytime during the trial with no charges.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;